// Small browser helpers shared by the on-device tools.

import { blobImageMime, withImageExtension } from '../imageFormat';
import { AiImageMeta, embedAiMetadata } from '../metadata/aiMetadata';

// Reads a Blob (e.g. the worker's PNG result) as a data URL, matching the
// representation the rest of the studio uses for results (MagicResult.imageUrl
// is a data URL — see geminiProvider). Keeps the result self-contained so it
// survives without an object URL to revoke.
export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
}

// Reads any image URL (data: or blob:) back into a Blob — used to persist a
// result / reference into IndexedDB as binary rather than an inflated base64
// string. Works for both data URLs and object URLs via a same-origin fetch.
export async function dataUrlToBlob(url: string): Promise<Blob> {
  const response = await fetch(url);
  return response.blob();
}

// Triggers a browser download of an image URL (data: or blob:) under a given
// filename — used by the "Download" footer action for any result. When `meta`
// is given, the AI-provenance XMP is embedded into the bytes first, so the
// marking travels with the downloaded file (see metadata/aiMetadata.ts).
//
// `filename`'s extension is re-pointed at the format the bytes really are:
// callers pass a default suffix, but the model decides the format (Nano Banana
// answers JPEG), so trusting the caller would save a JPEG named *.png.
export async function downloadImageUrl(
  url: string,
  filename: string,
  meta?: AiImageMeta,
): Promise<void> {
  let blob = await dataUrlToBlob(url);
  if (meta) {
    try {
      blob = await embedAiMetadata(blob, meta);
    } catch {
      // Embedding must never block a download — fall back to the raw bytes.
    }
  }
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = withImageExtension(filename, await blobImageMime(blob));
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after the click has been handled; immediate revoke can cancel it.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
}
